/*
 * useTealiumData - this hook prepares local analytics payloads and makes VirtualPage-View calls
 */

import { useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import sha256 from 'crypto-js/sha256';
import md5 from 'crypto-js/md5';
import { getLoggedUserMail, getItemDiscountedTotalPrice, getItemTotalPrice, getInsuranceBenefits, getDiscountNames, getDiscoutCodes, getInsuranceCode, convertNumber, getMd5Phone, getSha256Phone, isThereAnAddedWarranty, getOrderPromoCodeDiscountAmount } from '@utils/utils';
import * as actions from "@redux-actions/";

const useTealiumData = (page: string, elementToWatch) => {

    const dispatch = useDispatch();
    
    //get variables from redux store
    const formattedOrderItems = useSelector(state => state.cart.formattedOrderItems);
    const config = useSelector(state => state.cart.config);
    const person = useSelector(state => state.cart.person);
    const isMobile = useSelector(state => state.cart.isMobile);
    const isLoaded = useSelector(state => state.cart.isLoaded);
    const isEmptyCart = useSelector(state => state.cart.isEmptyCart);
    const clientSideNavigation = useSelector(state => state.cart.clientSideNavigation);
    const showPromoCodeFeedback = useSelector(state => state.cart.showPromoCodeFeedback);
    const cart = useSelector(state => state.cart.cart);

    //'dataToPush' state variable, used to populate obj for analytics call
    const [dataToPush, setDataToPush] = useState(null);
    const lastTrackedPageType = useRef<any>(null);

    const speedyOrderDetails = useSelector(state => state.cart.speedyOrderDetails);

    //getPageSections - this function get PageSections attributes for data object
    const getPageSections = (page) => {
        switch(page) {
            case 'Delivery':
            case 'Payment': 
                return {
                    Page_Section1: 'Checkout',
                    Page_Section2: 'Standard'
                }
            case 'Thankyou':
                return {
                    Page_Section1: 'Checkout',
                    Page_Section2: 'Standard'
                }
            default: 
                return {
                    Page_Section1: page,
                    Page_Section2: ''
                }
        }
    }

    //getPageDesign - this function get PageDesign attribute for data object
    const getPageDesign = (items) => {
        if(!items || items.length === 0) {
            return {
                Page_Design: 'Empty'
            }
        }
        return {};
    }

    //getUserStatusAndCryptoEmail - this function get user status and use encrypted email for data object
    const getUserStatusAndCryptoEmail = (user) => {
        if(!user || (!!user && !user.buyerDistinguishedName)) {
            return {
                User_LoginStatus: 'Guest',
                User_Email_MD5: !!user && !!user.orderItem[0].email1 ? md5(user.orderItem[0].email1.toLowerCase()).toString(): '',
                User_Email_SHA256: !!user && !!user.orderItem[0].email1 ? sha256(user.orderItem[0].email1.toLowerCase()).toString(): '',
            }
        }
        
        return {
            User_LoginStatus: 'Logged',
            User_Email_MD5: md5(getLoggedUserMail(user.buyerDistinguishedName)).toString(),
            User_Email_SHA256: sha256(getLoggedUserMail(user.buyerDistinguishedName)).toString()
        }
    }

    //getInsuranceAmount - this function get insurance amount for data object
    const getInsuranceAmount = (el) => {
        if(!el || !el.adjustment || el.adjustment.length === 0) return '0.00';

        const ins = el.adjustment.find(adj => adj.code === 'INSADJ');
        if(!!ins) {
            return convertNumber(ins.amount);
        } else {
            return '0.00';
        }
    }

    //getPrescriptionMethod - this function get the prescription method for data object
    const getPrescriptionMethod = (item) => {
        if(item[0].frame.xitem_frameType  !== 'RX') return '';     //occhiali senza prescription
        if(item[0].frame.xitem_prescriptionComplete === "true") return ''; //prescription inserita manualemente
        if(!!item[0].lenses.xitem_field2 && item[0].lenses.xitem_field2.length > 0) return 'skipped';  //prescription non inserita
        return 'upload'; //file prescription caricato
    }

    //getProducts - this function get all the products for populate 'Products' object in data object
    const getProducts = (items) => {
        if(items.length === 0) {
            return {}
        } 
        
        let partNumberUsed = [];
        const obj = {};
        items.map(item => {
            let productsPartNumber;
            if(partNumberUsed.find(p => item.frame.partNumber === p.partNumber) === undefined) {
                productsPartNumber = item.frame.partNumber;
                partNumberUsed.push({
                    partNumber: item.frame.partNumber,
                    count: 1
                })
            } else {
                const index = partNumberUsed.findIndex(p => item.frame.partNumber === p.partNumber);
                productsPartNumber = item.frame.partNumber + '#' + partNumberUsed[index].count;
                partNumberUsed[index].count = partNumberUsed[index].count + 1; 
            }

            const isItemSpeedyEligible = speedyOrderDetails?.glassesDetails?.some(glassesDetail => item.frame.orderItemId == glassesDetail.frameOrderItemId && glassesDetail?.speedyEligible) ?? false;
            
            obj[productsPartNumber] = {
                Status: item.buyable_frame ? 'Available' : 'Sold out',
                LensUPC: !!item.partNumber_lenses ? item.partNumber_lenses.replace('_SKU', '') : '', 
                Category:
                    item?.moCo?.prod_hierarchy_1 &&
                        typeof item.moCo.prod_hierarchy_1 === 'string' &&
                        item.moCo.prod_hierarchy_1.toLowerCase() === 'electronics'
                        ? 'Electronics'
                        : item?.frame?.category === 'EYE'
                            ? 'OPTICS'
                            : item?.frame?.category ?? '',
                Type: item.frame.xitem_frameType === 'RX' ? 'RX' : 'STD',
                FrameType: 'STD', 
                LensType: item.frame.xitem_frameType === 'RX' ? 'RX' : 'PLANO',
                Engraving: '',
                Price: getItemDiscountedTotalPrice(item), 
                PriceFull: getItemTotalPrice(item),
                Units: '1',
                Sku: item.model + item.color_code + item.size,
                ModelName: !!item.modelName ? item.modelName : '',
                Shape: !!item.frameshape ? item.frameshape : '',
                FrameTechnology: !!item.material ? item.material : '',
                FrameColor: !!item.color ? item.color : '',
                LensTechnology: !!item.rxc_lens_thickness ? item.rxc_lens_thickness : '',
                Warranty: !!cart && isThereAnAddedWarranty(cart, item.frame.orderItemId) ? convertNumber(cart.orderItem?.find(warrantyItem => (warrantyItem.comments == "WARRANTY" && warrantyItem.xitem_field1 == item.frame.orderItemId))?.orderItemPrice) : '0',
                Brand: !!item.brand ? item.brand : '',
                Preorder: '',
                Visibility: '',
                Badges: '',
                InsuranceAmount: getInsuranceAmount(elementToWatch),
                InsuranceCode: getInsuranceCode(),
                InsuranceBenefits: getInsuranceBenefits(),
                LensAntiblue: !!item.addOns && item.addOns.partNumbers.find(add => add === 'BlueLight_SKU') !== undefined ? 'Blue Light Treatment' : '',
                LensAntiReflective: !!item.addOns && item.addOns.partNumbers.find(add => add === 'CleanShieldElite_SKU') !== undefined ? 'Premium Anti-Reflective' : '',
                LensBrand: !!item.rxc_lens_brand ? item.rxc_lens_brand : '',
                LensColor: !!item.frame.xitem_rxcReviewObject && item.frame.xitem_rxcReviewObject.find(i => i.name === 'Color:') ? item.frame.xitem_rxcReviewObject.find(item => item.name === 'Color:').description : '',
                LensTreatment: !!item.frame.xitem_rxcReviewObject && item.frame.xitem_rxcReviewObject.find(i => i.name === 'Finishings:') && !!item.frame.xitem_rxcReviewObject.find(i => i.name === 'Finishings:').children && item.frame.xitem_rxcReviewObject.find(i => i.name === 'Finishings:').children.find(child => child.name === 'Scratch resistant') ? 'antiScratch' : '',
                ModelCode: !!item.model ? item.model : '',
                Moco: item.model + item.color_code,
                Lens: !!item.partNumber_lenses ? item.partNumber_lenses.replace('_SKU', '') : '',
                Frame: !!item.partNumber_frame ? item.partNumber_frame.replace('_SKU', '') :  '',
                Case: '',
                Size: item.size,
                Cloth: '',
                Image: !!item.frame.xitem_rxcImagery && !!item.frame.xitem_rxcImagery.productImage ? item.frame.xitem_rxcImagery.productImage : item.frame.xitem_imgUrl,
                Url: config.hostname + '/gl-us/eyewear/' + item.frame.partNumber,
                SpeedyRx_Eligible: isItemSpeedyEligible && !!item.lenses.xitem_rxcLensIsSpeedyRx && item.lenses.xitem_rxcLensIsSpeedyRx === 'true' ? 'Y' : ''
            }
        });

        return obj;
    }

    /*
      This hook is called every time dataToPush changes and makes Loading-Ready/VirtualPage-View
      calls based on the different pages    
    */
    useEffect(() => {
        if(dataToPush !== null) {
            var obj;

            if(page === 'CartPage') {
                //analytics should not be sent if user send promo code
                if(showPromoCodeFeedback === false) {
                    let analyticsCall = 'VirtualPage-View';
                    console.log('>>> ' + analyticsCall + ' tealium track. Page: ', page);
    
                    try {
                        obj = {
                            id: analyticsCall,
                            ...dataToPush
                        }
    
                    } catch(err) {
                        obj = {
                            id: 'Event',
                            Error_Source: 'Server',
                            Error_code: 'analytics payload',
                            Error_Detail: 'Error - VirtualPage-View',
                            Error_Message: 'Error - VirtualPage-View',
                            Events_Error: '1'
                        }
                    } finally {
                        var ria_0_cookie = RiaHelper.getCookie('ria_0');
                        var ria_1_cookie = RiaHelper.getCookie('ria_1');
                        var ins = !!elementToWatch && !!elementToWatch.adjustment ? elementToWatch.adjustment.find(adj => adj.code === 'INSADJ') : '';
                        if (!!ins || !!ria_0_cookie || !!ria_1_cookie){
                            if (getInsuranceAmount(elementToWatch)!='0.00'){                               
                                if (lastTrackedPageType.current !== obj.Page_Type){
                                    window?.tealium_data2track?.push(obj);
                                    lastTrackedPageType.current = obj.Page_Type;
                                    }
                            }                        
                        }else{
                            if (lastTrackedPageType.current !== obj.Page_Type){
                                window?.tealium_data2track?.push(obj);
                                lastTrackedPageType.current = obj.Page_Type;
                                }
                        }                        
                        setDataToPush(null);
                    }
                }
                
            } else {
                console.log('>>> VirtualPage-View tealium track. Page: ', page);

                var obj;
                try{
                    obj = {
                        id: 'VirtualPage-View', 
                        ...dataToPush
                    };
                }catch(err){
                    obj = {
                            id: 'Event',
                            Error_Source: 'Server',
                            Error_Code: 'analytics payload',
                            Error_Detail: 'Error - VirtualPage-View',
                            Error_Message: 'Error - VirtualPage-View',
                            Events_Error: '1'
                        };
                    }
                finally{
                    if (lastTrackedPageType.current !== obj.Page_Type){
                    window?.tealium_data2track?.push(obj);
                    lastTrackedPageType.current = obj.Page_Type;
                    }
                    setDataToPush(null);              
                }
            }
            
        }
    }, [dataToPush])

    /*
      This hook is called at the change of the page and when the page is loaded.
      It is use to create the local analytics payload.
    */
      useEffect(() => {
        console.log('>>>Analytics: ', page, isLoaded, dataToPush, elementToWatch);
        if(isLoaded === true){
            if(dataToPush === null) {
                let dataLayer = {};

                const dataLayerBasic ={
                    //Page
                    Page_Brand: config.brand,
                    Page_Country: config.country.toUpperCase(),
                    Page_Platform: 'WCS',
                    Store_Id: config.storeId,
                    Store_Brand: '',
                    Store_Custom: '',   
                    Page_Language: config.lang.toUpperCase(),
                    Page_DeviceType: isMobile ? 'M' : 'D',
                    Page_Type: page,
                    ...getPageSections(page),
                    ...getPageDesign(formattedOrderItems),
                    Page_PlatformRelease: 'Unknown',
                    Session_Status: '',
                    Session_Id: 'Unknown',
            
                    //User
                    ...getUserStatusAndCryptoEmail(elementToWatch),
                    User_EmailOptin: !!elementToWatch && !!elementToWatch.x_emailOptin && elementToWatch.x_emailOptin === "true" ? 1 : 0,
                    User_Country: !!person && !!person.contact && person.contact.length > 0 && !!person.contact[person.contact.length - 1].country ? person.contact[person.contact.length - 1].country : '',
                    User_Id: '',
                    User_Segments: '',
                    User_Phone_MD5: !!elementToWatch && !!elementToWatch.orderItem[0] && elementToWatch.orderItem[0].phone1 ? getMd5Phone(elementToWatch.orderItem[0].phone1) : '',
                    User_Phone_SHA256: !!elementToWatch && !!elementToWatch.orderItem[0] && elementToWatch.orderItem[0].phone1 ? getSha256Phone(elementToWatch.orderItem[0].phone1) : '',
                    User_HasPrescription: user != undefined && !!user.User_HasPrescription ? user.User_HasPrescription : "",
            
                    //Others
                    Page_UserFace: '',
                    Page_UserStyle: '',
                };
            
                if(!!config && !elementToWatch) {
                    //Cart Page Empty
                    dataLayer = {
                        ...dataLayerBasic,
            
                        //Order
                        Order_CartId: '',
                        Order_CartUrl: window.location.href,
                        Order_ProductsAmount: '0.00',
                        Order_DiscountAmount: '0.00',
                    }
            
                    setDataToPush(dataLayer);
                }
                    
                if(!!elementToWatch && !!config && !!formattedOrderItems) {
                    console.log('>>>Formatted order items', formattedOrderItems);
                    if(formattedOrderItems.length > 0) {
                        const numberOfClearance= formattedOrderItems.filter(item => !!item.frame.isClearance && item.frame.isClearance == true).length;
                        dataLayer = {
                            ...dataLayerBasic,
        
                            //Products
                            Products: getProducts(formattedOrderItems),
    
                            //Order
                            Order_CartId: elementToWatch.orderId,
                            Order_CartUrl: window.location.href.replace('PaymentView', 'CartView').replace('ShippingView', 'CartView'),
                            Order_Currency: elementToWatch.grandTotalCurrency,
                            Order_ProductsAmount: convertNumber(elementToWatch.totalProductPrice),
                            Order_DiscountName:  elementToWatch.orderItem[0].xitem_basePrice!="" && elementToWatch.orderItem[0].xitem_basePrice!=""!= elementToWatch.orderItem[0].unitPrice && !!numberOfClearance && numberOfClearance > 0 ? 'Clearance' + (getDiscountNames(elementToWatch.adjustment) ? ',' + getDiscountNames(elementToWatch.adjustment) : '') : getDiscountNames(elementToWatch.adjustment),
                        }; 
                        if (!!elementToWatch && !!elementToWatch.promotionCode){    
                            dataLayer = {
                                ...dataLayer,
                                Order_DiscountAmount: getOrderPromoCodeDiscountAmount(elementToWatch),
                            };
                        } else{                                
                            dataLayer = {
                                ...dataLayer,
                                Order_Subtotal: convertNumber(elementToWatch.totalProductPrice),
                                Order_Total: convertNumber(elementToWatch.grandTotal),
                                Order_DiscountAmount: '0',
                            };
                        };
                        if(page !== 'CartPage') {
                            dataLayer = {
                                    ...dataLayer,

                                Order_GiftCardCode: '', 
                                Order_DiscountCode: getDiscoutCodes(elementToWatch.promotionCode),
                                Order_ShippingDeliveryDate: elementToWatch.orderItem[0].expectedShipDate.split('T')[0],
                            }                        
                        }
            
                        if(page === 'Payment' || page === 'Thankyou') {
                            dataLayer = {
                                ...dataLayer,
                                Order_ShippingAmount: convertNumber(elementToWatch.totalShippingCharge),
                                Order_ShippingDiscount: '',
                                Order_ShippingTaxRate: '',
                                Order_ShippingMode: elementToWatch.orderItem[0].shipModeDescription.split(' ')[0] !== 'Collect' ? elementToWatch.orderItem[0].shipModeDescription.toLowerCase() === 'speedy rx' ?  elementToWatch.orderItem[0].shipModeDescription.replaceAll(' ', '') : elementToWatch.orderItem[0].shipModeDescription.split(' ')[0] : 'Click&Collect' ,
                                Order_State: elementToWatch.orderItem[0].state,
                                Order_ZipCode: elementToWatch.orderItem[0].zipCode,
                                Order_InsuranceAmount: getInsuranceAmount(elementToWatch),
                                Order_InsuranceCode: getInsuranceCode(),
                                Order_InsuranceBenefits: getInsuranceBenefits(),
                                Order_Insured: '',
                                Events_UserPhoneSub: typeof(window.smsChecked) != 'undefined' ? !window.smsChecked ? '1' : '0': '',

                            }
                        }
        
                        if(page === 'Thankyou') {
                            let payment_Type = !!elementToWatch.paymentInstruction ? elementToWatch.paymentInstruction[0].payMethodId.toLowerCase() : "glapplepay";
                            let paymentType;
                            
                            switch(payment_Type){
                                case "glklarna":
                                    paymentType = "Klarna";
                                    break;
                                case "glapplepay":
                                    paymentType = localStorage.getItem("Order_PaymentType");
                                    // paymentType = "ApplePay";
                                    break;
                                case "paypal":
                                    paymentType = localStorage.getItem("paymentIdPaypal");
                                    paymentType = paymentType.charAt(0).toUpperCase() + paymentType.slice(1);
                                    break;
                                default:
                                    paymentType = payment_Type;
                            }
                            dataLayer = {
                                ...dataLayer,
                                Order_PaymentType: paymentType,
                                Order_PaymentInstallments: !!elementToWatch.paymentInstruction && elementToWatch.paymentInstruction[0].payMethodId === 'Affirm' ? '3' : '',
                                Order_Type: '',
                                Order_SubscriptionFrequency: '0',
                                Order_PrescriptionMethod: getPrescriptionMethod(formattedOrderItems),
                                Order_Id: elementToWatch.orderId
                            }

                            console.log('>>>>Analytics data:', dataLayer);
                        }
                        
                        setDataToPush(dataLayer);
                    }
                }
            } 
           
        } else {
            if(isEmptyCart === true) {
                let dataLayer = {};

                const dataLayerBasic ={
                    //Page
                    Page_Brand: config.brand,
                    Page_Country: config.country.toUpperCase(),
                    Page_Platform: 'WCS',
                    Store_Id: config.storeId,
                    Store_Brand: '',
                    Store_Custom: '',   
                    Page_Language: config.lang.toUpperCase(),
                    Page_DeviceType: isMobile ? 'M' : 'D',
                    Page_Type: page,
                    ...getPageSections(page),
                    ...getPageDesign(formattedOrderItems),
                    Page_PlatformRelease: 'Unknown',
                    Session_Status: '',
                    Session_Id: 'Unknown',
            
                    //User
                    ...getUserStatusAndCryptoEmail(elementToWatch),
                    User_EmailOptin: !!elementToWatch && !!elementToWatch.x_emailOptin && elementToWatch.x_emailOptin === "true" ? 1 : 0,
                    User_Country: !!person && !!person.contact && person.contact.length > 0 && !!person.contact[person.contact.length - 1].country ? person.contact[person.contact.length - 1].country : '',
                    User_Id: '',
                    User_Segments: '',
                    User_Phone_MD5: !!elementToWatch && !!elementToWatch.orderItem[0] && elementToWatch.orderItem[0].phone1 ? getMd5Phone(elementToWatch.orderItem[0].phone1) : '',
                    User_Phone_SHA256: !!elementToWatch && !!elementToWatch.orderItem[0] && elementToWatch.orderItem[0].phone1 ? getSha256Phone(elementToWatch.orderItem[0].phone1) : '',
                    User_HasPrescription: user != undefined && !!user.User_HasPrescription ? user.User_HasPrescription : "",
            
                    //Others
                    Page_UserFace: '',
                    Page_UserStyle: '',
                };
            
                
                    //Cart Page Empty
                dataLayer = {
                    ...dataLayerBasic,
        
                    //Order
                    Order_CartId: '',
                    Order_CartUrl: window.location.href,
                    Order_ProductsAmount: '0.00',
                    Order_DiscountAmount: '0.00',
                }
            
                setDataToPush(dataLayer);
            }
        }
    }, [page, isLoaded, isEmptyCart, showPromoCodeFeedback, formattedOrderItems]);

}

export default useTealiumData;
